import * as React from "react";
import { Hint, HintProps } from "react-ui";

import { getHintWidth } from "../../constants";
import { useCandyTheme } from "../ThemeProvider/CandyTheme/UseCandyTheme";
import { LengthUnit } from "../ThemeProvider/CandyTheme/Utils/CssLength";
import { useDomElementState } from "../../Commons/DOM/UseDomElementState";
import { DomElementHack } from "../../Commons/DOM/DomElementHack";
import { isNullOrWhiteSpace } from "../../../../../Common/TypingUtils";

export type HintPos = HintProps["pos"];

interface TextOverflowHintProps {
    text: string;
    textComponent?: JSX.Element;
    children?: JSX.Element;
    showHint?: boolean;
    width?: number | string;
    pos?: HintPos;
    hintMaxWidth?: number;
    useChildDomElementValue?: boolean;
}

export function TextOverflowHint(props: TextOverflowHintProps): JSX.Element {
    const theme = useCandyTheme();
    const [elementRef, domElement] = useDomElementState<DomElementHack>();

    const childInput = React.useMemo(() => domElement?.querySelector("input") ?? undefined, [domElement]);
    const childButton = React.useMemo(() => domElement?.querySelector("button") ?? undefined, [domElement]);

    const checkOverflow = (text?: string | null): boolean => {
        if (isNullOrWhiteSpace(text)) {
            return false;
        }
        const textWidth = theme.typography.getEstimatedTextWidthInPxForPrimaryFont(text);
        if (props.width && typeof props.width === "number") {
            return textWidth > props.width;
        }
        if (childInput) {
            const inputTextWidth = props.useChildDomElementValue
                ? theme.typography.getEstimatedTextWidthInPxForPrimaryFont(childInput.value)
                : textWidth;
            return inputTextWidth > childInput.clientWidth;
        }
        if (childButton) {
            const buttonOversize =
                theme.reactUISizes.button.paddingLeft.getValue(LengthUnit.px) +
                theme.reactUISizes.button.paddingRight.getValue(LengthUnit.px) +
                2;
            return textWidth > childButton.clientWidth - buttonOversize;
        }
        return false;
    };

    const textOverflow = React.useMemo(
        () => checkOverflow(props.text),
        [props.text, props.width, childInput, childButton]
    );

    return (
        <DomElementHack ref={elementRef}>
            <Hint
                maxWidth={props.hintMaxWidth ?? getHintWidth(props.text.length)}
                text={
                    (props.showHint ?? true) && textOverflow
                        ? props.textComponent
                            ? props.textComponent
                            : props.text
                        : ""
                }
                pos={props.pos ?? "top"}>
                {props.children}
            </Hint>
        </DomElementHack>
    );
}
